

















































































import { computed, defineComponent, PropType, ref, toRef, watch } from "@vue/composition-api";
import { format, getMinutes, setMinutes, startOfDay } from "date-fns";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { AppointmentSlot } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentSlotProvider";
import {
  AppointmentType,
  AppointmentTypeKind,
  AppointmentTypeList,
} from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentTypeProvider";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getCostByCurrency } from "@/lib/moneyHelpers";
import { formatDate } from "@/lib/time";
import { ListFields, Tab } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseInput from "@/components/BaseInput";
import BaseSegmentedButton from "@/components/BaseSegmentedButton";
import BaseSelect from "@/components/BaseSelect";
import BaseSpinner from "@/components/BaseSpinner";
import BaseTabs from "@/components/BaseTabs";
import CustomScroll from "@/components/CustomScroll";
import Divider from "@/components/Divider";
import ModalView from "@/components/ModalView";
import QuickTipsItem from "@/components/QuickTipsItem";
import TimePickerButton from "@/components/TimePickerButton";
import UserBadgeWithAvatar from "@/components/UserBadgeWithAvatar";

export default defineComponent({
  name: "UserBookAppointmentModalView",
  components: {
    BaseTabs,
    BaseSelect,
    UserBadgeWithAvatar,
    TimePickerButton,
    Divider,
    QuickTipsItem,
    BaseSegmentedButton,
    BaseConfirm,
    ModalView,
    BaseInput,
    CustomScroll,
    BaseButton,
    BaseSpinner,
  },
  props: {
    modalLoading: { type: Boolean, required: true },
    userId: { type: String, required: true },
    user: { type: Object as PropType<UserPublicData>, required: true },
    paymentPackages: { type: Array as () => PaymentPackage[], required: true },
    appointmentTypes: { type: Object as () => AppointmentTypeList | null },
  },
  setup(props) {
    const loading = ref(false);
    const selectDate = ref(format(new Date(), "yyyy-MM-dd"));
    const activeSlot = ref<AppointmentSlot>();
    const selectedOption = ref("");
    const appointmentSlots = ref<AppointmentSlot[]>([]);
    const appointmentDuration = ref(0);
    const showConfirm = ref(false);
    const tabs = ref<Tab[]>([]);
    const appointmentTypeTab = ref<Tab[]>([]);
    const appointmentTypeTabActive = ref("Online");
    const activeTab = ref<AppointmentTypeKind>(AppointmentTypeKind.MESSAGING);

    const getSlotTime = (date: string) => {
      return formatDate(new Date(date), { hour: "numeric", minute: "numeric" });
    };

    const activeTime = computed(() => getSlotTime(activeSlot.value?.startTime ?? ""));

    const appointmentsFilterByType = computed(() => {
      return props.appointmentTypes?.types.filter(item => item.kind === activeTab.value) ?? [];
    });

    const selectedAppointmentType = computed<AppointmentType | undefined>(() =>
      appointmentsFilterByType.value?.find(appointmentType => appointmentType.id === selectedOption.value)
    );

    const selectStartTime = computed(() => {
      return activeSlot.value?.startTime;
    });

    const getAppointmentSlots = async (): Promise<void> => {
      try {
        loading.value = true;
        appointmentSlots.value = [];
        const date = new Date(selectDate.value);
        if (selectedAppointmentType.value) {
          appointmentSlots.value = await providers.AppointmentSlotProvider.search({
            data: {
              userId: props.userId,
              startTime: startOfDay(date).toISOString(),
              endTime: startOfDay(date.setDate(date.getDate() + 1)).toISOString(),
              appointmentTypeId: selectedAppointmentType.value?.id,
            },
          });
        }
      } finally {
        loading.value = false;
      }
    };

    const selectTime = (slot: AppointmentSlot) => {
      activeSlot.value = slot;
    };

    const packageOptions = computed<ListFields>(() => {
      return appointmentsFilterByType.value.map(type => {
        let findPackage = props.paymentPackages.find(item => item.id === type.pricePackageId);
        return {
          text: `${type.duration > 60 ? `${type.duration / 60} hours` : `${type.duration} min`} / ${formatMoneyString(
            findPackage ? getCostByCurrency(findPackage)?.cost || 0 : 0
          )}`,
          value: type.id,
        };
      });
    });

    const activeTimeRange = computed(() => {
      const selectedDuration = selectedAppointmentType.value?.duration;
      if (activeSlot.value && selectedDuration) {
        const appointmentEndTime = setMinutes(
          new Date(activeSlot.value.startTime),
          getMinutes(new Date(activeSlot.value.startTime)) + selectedDuration
        );
        return `${activeTime.value.toLowerCase()} - ${getSlotTime(appointmentEndTime.toISOString()).toLowerCase()}`;
      }
    });

    const selectOption = (newValue: string) => {
      selectedOption.value = newValue;
    };
    const changeActiveTab = (tab: AppointmentTypeKind) => {
      activeTab.value = tab;
      activeSlot.value = undefined;
      selectedOption.value = packageOptions.value[0].value;
    };

    const changeAppointmentTypeTab = (value: string) => {
      appointmentTypeTabActive.value = value;
      if (value === "Offline") {
        changeActiveTab(AppointmentTypeKind.TOUR_GUIDE);
      } else {
        changeActiveTab(tabs.value[0].value);
      }
    };

    watch([selectedOption, selectDate], async () => {
      await getAppointmentSlots();
    });

    const getAvailableAppointmentTabs = () => {
      tabs.value = [
        { value: AppointmentTypeKind.MESSAGING, text: $t("Chat") },
        { value: AppointmentTypeKind.VIDEO_CALLS, text: $t("Video") },
        { value: AppointmentTypeKind.AUDIO_CALLS, text: $t("Audio") },
      ].filter(item => !!props.appointmentTypes?.types.find(i => i.kind === item.value));
      if (
        props.appointmentTypes?.types.some(
          item =>
            item.kind === AppointmentTypeKind.MESSAGING ||
            item.kind === AppointmentTypeKind.AUDIO_CALLS ||
            item.kind === AppointmentTypeKind.VIDEO_CALLS
        )
      ) {
        appointmentTypeTab.value = [...appointmentTypeTab.value, { text: $t("Online"), value: "Online" }];
        changeActiveTab(tabs.value[0].value);
      }
      if (props.appointmentTypes?.types.some(item => item.kind === AppointmentTypeKind.TOUR_GUIDE)) {
        appointmentTypeTab.value = [...appointmentTypeTab.value, { text: $t("Offline"), value: "Offline" }];
      }
      changeAppointmentTypeTab(appointmentTypeTab.value[0].value);
    };

    watch(toRef(props, "appointmentTypes"), (value, oldValue) => {
      if (!!value && !oldValue) {
        getAvailableAppointmentTabs();
      }
    });

    return {
      AppointmentTypeKind,
      loading,
      tabs,
      activeTab,
      selectDate,
      activeSlot,
      showConfirm,
      packageOptions,
      selectedOption,
      selectStartTime,
      activeTimeRange,
      appointmentSlots,
      appointmentTypeTab,
      appointmentDuration,
      selectedAppointmentType,
      appointmentTypeTabActive,
      selectTime,
      getSlotTime,
      selectOption,
      changeActiveTab,
      getAppointmentSlots,
      changeAppointmentTypeTab,
    };
  },
});
